<template>
  <v-container fluid fill-height style="height:calc(100vh - 64px)">
    <v-row style="height: 100%; width: 100%">
      <v-col cols="12" md="6" style="height: 100%">
        <v-card ref="content" height="100%" id="parcels-card" :style="getCardGrid">
          <v-row ref="title" no-gutters>
            <v-col class="pa-3 d-flex justify-space-between align-center" cols="12">
              <span class="headline">{{ $i18n.t('parcels.title') }}</span>
              <v-badge :value="filtersLength > 0" class="float-right" color="red" right overlap>
                <template v-slot:badge>
                  <span v-text="filtersLength" />
                </template>
                <v-btn
                  class="float-right btn--filter"
                  fab
                  dark
                  :title="$i18n.t('parcels.addFilters')"
                  small
                  color="primary"
                  @click.stop="filtersVisible = true"
                >
                  <v-icon dark>mdi-filter-variant</v-icon>
                </v-btn>
                <v-btn
                  class="float-right mr-1"
                  v-show="!isDrawingActive"
                  fab
                  dark
                  :title="$i18n.t('parcels.drawParcel')"
                  @click="startDrawingPolygon"
                  small
                  color="primary"
                >
                  <v-icon dark>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  class="float-right mr-1"
                  fab
                  dark
                  :title="$i18n.t('dashboard.map.accept')"
                  v-show="isDrawingActive && !isFilter"
                  @click="acceptDrawedPolygon()"
                  small
                  color="primary"
                >
                  <v-icon dark>mdi-check</v-icon>
                </v-btn>
                <v-btn
                  class="float-right mr-1"
                  fab
                  dark
                  v-show="isDrawingActive && !isFilter"
                  :title="$i18n.t('dashboard.map.cancel')"
                  @click="cancelDrawingPolygon()"
                  small
                  color="primary"
                >
                  <v-icon dark>mdi-close</v-icon>
                </v-btn>
                <v-btn
                  v-if="activeTab === 0"
                  fab
                  small
                  class="float-right mr-1"
                  color="primary"
                  :title="$i18n.t('dialog.header.massEditingOfAttributes')"
                  @click.stop="isFieldCalculatorVisible = true"
                >
                  <v-icon dark>mdi-calculator-variant-outline</v-icon>
                </v-btn>
              </v-badge>
            </v-col>
          </v-row>
          <v-row ref="filters" class="pr-3 pl-3">
            <v-col cols="12" md="4" class="pb-0">
              <v-select
                :attach="true"
                clearable
                v-model="wojewodztwo"
                :items="provinces"
                item-text="jpt_nazwa_"
                return-object
                @change="getPRG('counties')"
                :label="$i18n.t('dashboard.offers.woj')"
                :no-data-text="$i18n.t('default.noDataText')"
                outlined
              />
            </v-col>
            <v-col cols="12" md="4" class="pb-0">
              <v-select
                :attach="true"
                clearable
                v-model="powiat"
                :items="counties"
                item-text="jpt_nazwa_"
                return-object
                @change="getPRG('communes')"
                item-value="jpt_kod_je"
                :label="$i18n.t('dashboard.offers.pow')"
                :no-data-text="$i18n.t('default.noDataText')"
                outlined
                :disabled="!wojewodztwo"
              />
            </v-col>
            <v-col cols="12" md="4" class="pb-0">
              <v-select
                :attach="true"
                clearable
                v-model="gmina"
                :items="communes"
                :label="$i18n.t('dashboard.offers.gmi')"
                outlined
                return-object
                :disabled="!powiat"
                item-text="jpt_nazwa_"
                :no-data-text="$i18n.t('default.noDataText')"
              />
            </v-col>
          </v-row>

          <v-row no-gutters class="pa-2 justify-end">
            <v-col cols="auto">
              <v-tooltip bottom allow-overflow content-class="tooltip__span">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-btn
                      small
                      text
                      outlined
                      color="primary"
                      class="pr-4"
                      @click="createOffer"
                      :loading="creatingOffer"
                      :disabled="!isAnyParcelChecked"
                      :class="{
                        'project__button--active': isAnyParcelChecked
                      }"
                    >{{ $i18n.t(`offer.parcels.createOffer`) }}</v-btn>
                  </div>
                </template>
                <span>{{ titleOffer }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pt-0" md="12" sm="12">
              <v-tabs ref="tabs_1" v-model="activeTab">
                <v-tab>{{ $i18n.t('offer.parcels.title') }}</v-tab>
                <v-tab>{{ $i18n.t('dashboard.source.owners') }}</v-tab>
              </v-tabs>
            </v-col>
          </v-row>
          <template v-if="!contentLoading && !tableLoading">
            <v-row no-gutters align="center" v-if="activeTab === 0">
              <v-col cols="auto" class="pl-3">
                <v-switch v-model="isMapViewFilter" />
              </v-col>
              <v-col
                cols="auto"
                class="text-body-2 pl-2"
              >{{$i18n.t('dashboard.offers.toggleMapViewFilter')}}</v-col>
            </v-row>
          </template>
          <v-row ref="table" no-gutters class="overflow-y-auto">
            <template v-if="contentLoading || tableLoading">
              <v-col cols="12">
                <v-row no-gutters justify="center" align="center" style="height: 100%">
                  <v-col cols="auto">
                    <v-progress-circular indeterminate color="primary" />
                  </v-col>
                </v-row>
              </v-col>
            </template>
            <template v-else>
              <v-col v-if="activeTab===0" cols="12" style="height: 100%;">
                <div
                  style="height: 100%; display: flex; justify-content: center; align-items: center;"
                  v-if="parcels.length < 1"
                >
                  <v-progress-circular indeterminate color="primary" />
                </div>
                <CustomDataTable
                  ref="parcelsTable"
                  :items="filteredFeaturesInView"
                  :headers="parcelsHeaders"
                  :selectable="true"
                  entity-type="offer.parcels"
                  max-height="90vh"
                  v-model="ids"
                  :select-interaction="true"
                  :parentClickedRow="selectedRow"
                  @rowClicked="onRowClicked($event)"
                >
                  <template v-slot:tools="{ item }">
                    <v-icon
                      small
                      class="mr-2"
                      :title="$i18n.t(`parcels.table.details`)"
                      @click="
                            $router.push({
                              name: 'parcel',
                              params: { id: item.id }
                            })
                          "
                    >mdi-eye-outline</v-icon>
                    <v-icon
                      class="mr-2"
                      :title="$i18n.t(`parcels.table.zoomToParcel`)"
                      small
                      @click="zoomToParcel(item)"
                    >mdi-magnify-plus-outline</v-icon>
                    <v-icon
                      small
                      class="mr-2"
                      :title="$i18n.t(`parcels.table.copy`)"
                      @click="copyParcelDialog(item)"
                    >mdi-content-copy</v-icon>
                    <v-icon
                      class="mr-2"
                      :title="$i18n.t(`parcels.table.delete`)"
                      small
                      @click="prepareDeleteParcel(item)"
                    >mdi-delete</v-icon>
                  </template>
                </CustomDataTable>
              </v-col>

              <v-col v-if="activeTab===1" cols="12" style="height: 100%;">
                <v-data-table
                  id="table-owners"
                  class="overflow-y-auto"
                  :headers="ownersMeta"
                  :loading="tableLoading"
                  :items="filteredOwners"
                  :single-expand="true"
                  item-key="id"
                  show-expand
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  v-bar
                  @item-expanded="ownerExpanded"
                  :no-data-text="$i18n.t('default.noDataText')"
                >
                  <template
                    v-slot:header.wlasciciel.pl="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.wlasciciel.en="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.adres="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.email="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.telefon="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="pa-0">
                      <v-data-table
                        v-if="item.id === expandedItem"
                        id="table-offers"
                        class="overflow-y-auto table-offers--panel"
                        :headers="ownerParcelsHeaders"
                        :loading="tableLoading"
                        :items="ownerParcels"
                        disable-pagination
                        hide-default-footer
                        fixed-header
                        :no-data-text="$i18n.t('default.noDataText')"
                      >
                        <template
                          v-slot:header.nr_ew_dzialek="{ header }"
                        >{{ $i18n.t(`offer.parcels.table.${header.text}`) }}</template>
                        <template v-slot:header.nr_obre>{{ $i18n.t('offer.parcels.table.nr_obre') }}</template>
                        <template
                          v-slot:header.powierzchnia="{ header }"
                        >{{ $i18n.t(`offer.parcels.table.${header.text}`) }}</template>
                        <template
                          v-slot:header.wojewodztwo="{ header }"
                        >{{ $i18n.t(`offer.parcels.table.${header.text}`) }}</template>
                        <template v-slot:header.tools>
                          <v-icon class="subtitle-2 pb-1 align-center">mdi-tools</v-icon>
                        </template>
                        <template v-slot:body="{ items }">
                          <tbody>
                            <template v-if="items.length > 0">
                              <tr
                                v-for="item in items"
                                :key="item.id"
                                @click="onRowClicked(item.id)"
                                :class="{
                              'table__row--background': item.id === selectedRow
                            }"
                              >
                                <Attribute :item="item.nr_ew_dzialek" />
                                <Attribute :item="item.nr_obre" />
                                <Attribute :item="item.powierzchnia" />
                                <Attribute :item="item.wojewodztwo" />

                                <td class="d-flex justify-center">
                                  <v-icon
                                    small
                                    class="mr-2"
                                    :title="$i18n.t(`parcels.table.details`)"
                                    @click="
                                  $router.push({
                                    name: 'parcel',
                                    params: { id: item.id }
                                  })
                                "
                                  >mdi-eye-outline</v-icon>
                                  <v-icon
                                    class="mr-2"
                                    :title="$i18n.t(`parcels.table.zoomToParcel`)"
                                    small
                                    @click="zoomToParcel(item)"
                                  >mdi-magnify-plus-outline</v-icon>
                                </td>
                              </tr>
                            </template>
                            <template v-else>
                              <v-subheader
                                class="justify-center"
                                v-text="$i18n.t('default.noDataText')"
                              ></v-subheader>
                            </template>
                          </tbody>
                        </template>
                      </v-data-table>
                    </td>
                  </template>
                </v-data-table>
              </v-col>
            </template>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" style="height: inherit">
        <v-card height="100%">
          <v-row no-gutters style="height: 100%">
            <v-col class="pa-1" style="height: inherit">
              <div style="height:100%;width:100%">
                <ParcelsMap
                  ref="map"
                  :parcels="parcelsFiltered"
                  @onSelectedId="selectedRowOnMap"
                  @clearSelection="clearSelection"
                  @filteredParcels="setFilteredParcels"
                  @filteredFeaturesWithView="setFeaturesInView"
                  @cancelFiltering="filteredParcels = []"
                />
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <FieldFilter
      :is-visible.sync="filtersVisible"
      :entities="filtersConfig.entities"
      :entitiesType="filtersConfig.entitiesType"
      :fields="filtersConfig.properties"
      :public-hints="filtersConfig.publicHints"
      @setFiltersBadge="filtersLength = $event"
      @setFilteredEntities="setFilteredEntities"
    />
    <ConfirmDialog
      v-if="confirmDialogLoaded"
      :dialog="confirmDialogVisible"
      @success="deleteParcel()"
      @cancel="confirmDialogVisible = false"
    >
      <template v-slot:header>
        <h1>{{ $i18n.t('parcels.deleteDialog.title') }}</h1>
      </template>
      <template v-slot:content>
        <p>{{ $i18n.t('parcels.deleteDialog.content') }}</p>
      </template>
    </ConfirmDialog>
    <ConfirmDialog
      v-if="copyDialogLoaded"
      :dialog="copyDialogVisible"
      @success="copyParcel()"
      @cancel="copyDialogVisible = false"
    >
      <template v-slot:header>
        <h1>{{ $i18n.t(`parcels.copyDialog.title`) }}</h1>
      </template>
      <template v-slot:content>
        <p>{{ $i18n.t(`parcels.copyDialog.contentQuestion`) }}</p>
      </template>
    </ConfirmDialog>
    <FieldCalculator
      :features-ids="filteredParcelsIds"
      :is-visible.sync="isFieldCalculatorVisible"
      :content-text="$i18n.t('dialog.content.allFilteredParcelsWillBeEdited', { length: filteredParcelsIds.length })"
      entity="parcels"
      @success="reloadParcels"
    />
  </v-container>
</template>

<script>
import ParcelsMap from '@/components/parcels/ParcelsMap';
import FieldFilter from '@/components/FieldFilter';
import { wojOptions } from '@/models/Offer';
import Attribute from '@/components/dashboard/Attribute';
import CustomDataTable from '@/components/CustomDataTable';

const TYPE = function(obj) {
  return {}.toString
    .call(obj)
    .match(/\s([a-zA-Z]+)/)[1]
    .toLowerCase();
};

export default {
  components: {
    ConfirmDialog: () => import('@/components/ConfirmDialog'),
    FieldCalculator: () => import('@/components/FieldCalculator'),
    ParcelsMap,
    FieldFilter,
    CustomDataTable,
    Attribute
  },
  data: () => ({
    isFieldCalculatorVisible: false,
    creatingOffer: false,
    filteredParcels: [],
    // visibleParcelsIDs: [],
    featuresInView: [],
    isParcelEditing: false,
    isLegendActive: false,
    isPrintingVisible: false,
    tableLoading: true,
    filtersVisible: false,
    // filters: [],
    filtersLength: null,
    selectedRow: undefined,
    //PRG
    provinces: [],
    counties: [],
    communes: [],

    wojewodztwo: undefined,
    powiat: undefined,
    gmina: undefined,

    parcels: [],

    // create offer
    ids: [],
    pl: '',
    en: '',

    itemToDelete: undefined,
    map: undefined,
    expandedItem: null,
    description: '',
    // item: undefined,

    // Components settings variables
    isDefaultDialogLoaded: false,
    isDefaultDialogVisible: false,
    confirmDialogLoaded: false,
    confirmDialogVisible: false,
    isDrawingActive: false,
    isFilter: undefined,
    parcelsHeaders: [
      {
        text: 'nr_ew_dzialek',
        value: 'nr_ew_dzialek',
        type: 'text',
        translate: false
      },
      {
        text: 'nr_obre',
        value: 'nr_obre',
        type: 'text',
        translate: false
      },
      {
        text: 'powierzchnia',
        value: 'powierzchnia',
        type: 'number',
        translate: false
      },
      {
        text: 'wojewodztwo',
        value: 'wojewodztwo',
        type: 'text',
        translate: false
      }
    ],

    copyDialogLoaded: false,
    copyDialogVisible: false,
    copyDialogItem: undefined,
    parcelsFilteredInModal: [],
    activeTab: 0,

    contentLoading: true,

    owners: [],
    filteredOwners: [],
    ownerParcels: [],
    ownersMeta: [
      {
        text: 'ownerPl',
        value: 'wlasciciel.pl',
        type: 'text'
      },
      {
        text: 'ownerEn',
        value: 'wlasciciel.en',
        type: 'text'
      },
      {
        text: 'address',
        value: 'adres',
        type: 'text'
      },
      {
        text: 'email',
        value: 'email',
        type: 'text'
      },
      {
        text: 'phone',
        value: 'telefon',
        type: 'text'
      }
    ],
    ownerParcelsHeaders: [
      {
        text: 'nr_ew_dzialek',
        value: 'nr_ew_dzialek',
        type: 'text',
        translate: false
      },
      {
        text: 'nr_obre',
        value: 'nr_obre',
        type: 'text',
        translate: false
      },
      {
        text: 'powierzchnia',
        value: 'powierzchnia',
        type: 'number',
        translate: false
      },
      {
        text: 'wojewodztwo',
        value: 'wojewodztwo',
        type: 'text',
        translate: false
      },
      {
        text: 'tools',
        value: 'tools',
        align: 'center',
        sortable: false
      }
    ]
  }),
  computed: {
    isMapViewFilter: {
      get() {
        return this.$store.getters.getParcelsMapViewFilter;
      },
      set(nV) {
        this.$store.commit('setParcelsMapViewFilter', nV);
      }
    },
    filteredParcelsIds() {
      return this.filteredFeaturesInView.map(feature => feature.id);
    },
    filtersConfig() {
      if (this.activeTab === 0) {
        return {
          entitiesType: 'parcels',
          entities: this.parcels,
          properties: this.parcelsHeaders.map(header => {
            const { text, translate, type, value } = header;
            return {
              name: value,
              type,
              translate,
              label_pl: this.$i18n.t(`offer.parcels.table.${header.text}`),
              label_en: this.$i18n.t(`offer.parcels.table.${header.text}`)
            };
          })
        };
      } else {
        return {
          entitiesType: 'owners',
          entities: this.owners,
          properties: this.ownersMeta.map(header => {
            const { text, translate, type, value } = header;
            return {
              name: value,
              type,
              translate,
              label_pl: this.$i18n.t(`dashboard.owners.table.${header.text}`),
              label_en: this.$i18n.t(`dashboard.owners.table.${header.text}`)
            };
          })
        };
      }
    },
    rules() {
      return v => !!v || this.$i18n.t('default.requiredField');
    },
    isAnyParcelChecked() {
      return this.ids.length > 0;
    },
    titleOffer() {
      return this.isAnyParcelChecked
        ? this.$i18n.t(`offer.parcels.createOffer`)
        : this.$i18n.t(`offer.parcels.chooseParcel`);
    },
    wojOptions() {
      return [{ value: '', name: this.$i18n.t('parcels.filters.all') }].concat(wojOptions);
    },
    parcelsFiltered() {
      let parcelsFiltered = Array.from(this.parcelsFilteredInModal);
      parcelsFiltered = this.filterByPRG('wojewodztwo', parcelsFiltered);
      parcelsFiltered = this.filterByPRG('powiat', parcelsFiltered);
      parcelsFiltered = this.filterByPRG('gmina', parcelsFiltered);
      if (this.filteredParcels.length > 0) {
        parcelsFiltered = parcelsFiltered.filter(offer => this.filteredParcels.includes(offer.id));
      }
      return parcelsFiltered;
    },
    filteredFeaturesInView() {
      return this.isMapViewFilter
        ? this.parcelsFiltered.filter(f => this.featuresInView.includes(f.id))
        : this.parcelsFiltered;
    },
    getCardGrid() {
      if (this.activeTab == '0' && !this.contentLoading && !this.tableLoading) {
        return 'grid-template-rows: 55px 100px auto auto 50px 1fr;';
      } else {
        return 'grid-template-rows: 55px 100px auto auto 1fr;';
      }
    }
  },
  watch: {
    activeTab(nV) {
      this.contentLoading = true;
      setTimeout(() => {
        this.contentLoading = false;
      }, 1500);
    },
    wojewodztwo(newVal) {
      if (!newVal) {
        this.gmina = undefined;
        this.powiat = undefined;
        this.$store.commit('setParcelWoj', undefined);
        this.$store.commit('setParcelPowiat', undefined);
        this.$store.commit('setParcelGmina', undefined);
      } else {
        this.$store.commit('setParcelWoj', newVal);
        this.$store.commit('setParcelPowiat', undefined);
        this.$store.commit('setParcelGmina', undefined);
      }
    },
    powiat(newVal) {
      if (!newVal) {
        this.gmina = undefined;
        this.$store.commit('setParcelPowiat', undefined);
        this.$store.commit('setParcelGmina', undefined);
      } else {
        this.$store.commit('setParcelPowiat', newVal);
        this.$store.commit('setParcelGmina', undefined);
      }
    },
    gmina(newVal) {
      if (!newVal) {
        this.$store.commit('setParcelGmina', undefined);
      } else {
        this.$store.commit('setParcelGmina', newVal);
      }
    }
    // filters(newVal) {
    //   if (newVal.length === 0) {
    //     this.$store.commit('setParcelFilters', []);
    //   } else {
    //     this.$store.commit('setParcelFilters', newVal);
    //   }
    // }
  },
  mounted() {
    this.$store.dispatch('getOwners').then(r => {
      if (r.status === 200) {
        this.owners = r.data.owners;
        this.tableLoading = false;
      }
    });
    this.$store
      .dispatch('getPRG', {
        level: 'provinces'
      })
      .then(r => {
        this.provinces = r.data.provinces;
      });
    if (this.$store.getters.getParcelWoj) {
      this.wojewodztwo = this.$store.getters.getParcelWoj;
      this.getPRG('counties');
    }
    if (this.$store.getters.getParcelPowiat) {
      this.powiat = this.$store.getters.getParcelPowiat;
      this.getPRG('communes');
    }
    if (this.$store.getters.getParcelGmina) {
      this.gmina = this.$store.getters.getParcelGmina;
    }
    // if (this.$store.getters.getParcelFilters) {
    //   this.filters = this.$store.getters.getParcelFilters;
    // }
    this.parcels = [];
    this.$store.dispatch('getParcels', { all_attributes: true, geometry: true }).then(data => {
      this.parcels = data;
      this.visibleParcelsIDs = data.map(el => el.id);
      this.tableLoading = false;
      this.contentLoading = false;
    });
  },
  methods: {
    ownerExpanded(e) {
      if (e.value) {
        this.ownerParcels = [];
        this.ownerParcels = this.parcels.filter(parcel => parcel.wlasciciele && parcel.wlasciciele.includes(e.item.id));
        this.expandedItem = e.item.id;
      }
    },
    copyParcelDialog(item) {
      this.copyDialogItem = item;
      this.copyDialogLoaded = true;
      this.copyDialogVisible = true;
    },
    setFilteredEntities(data) {
      if (this.activeTab === 0) {
        this.parcelsFilteredInModal = data;
      } else {
        this.filteredOwners = data;
      }
    },
    copyParcel() {
      let item = this.copyDialogItem;
      this.$store.dispatch('copyParcel', item.id).then(r => {
        this.$router.push({ name: 'parcel', params: { id: r.data.parcels } });
      });
    },
    // Wykonaj odpowiednią akcję z geometrią narysowanego poligonu
    acceptDrawedPolygon() {
      if (this.isFilter) {
        this.$refs.map.filterWithArea();
      } else {
        this.$refs.map.addParcel();
        this.isDrawingActive = false;
      }
    },
    // Cancel adding description
    cancelDescription() {
      this.description = '';
      this.confirmDialogVisible = false;
    },
    // Wyłącz narzędzie rysowania poligonu
    cancelDrawingPolygon() {
      this.isFilter = false;
      this.isDrawingActive = false;
      this.$refs.map.cancelDrawingPolygon();
      this.filteredParcels = [];
    },
    changeBaselayer(baselayer) {
      this.$refs.map.changeBaselayer(baselayer);
    },
    // clearFilter() {
    //   this.filters = [];
    //   this.filtersVisible = false;
    // },
    clearSelection() {
      this.selectedRow = undefined;
      this.$refs.map.selectParcelById(undefined);
    },
    async deleteParcel() {
      try {
        const response = await this.$store.dispatch('deleteParcel', {
          id: this.itemToDelete.id
        });
        const index = this.parcels.indexOf(this.itemToDelete);
        this.parcels.splice(index, 1);
        this.confirmDialogVisible = false;
        this.$refs.map.clearSelection();
      } catch (error) {
        this.$root.$emit('showSnackbar', error);
      }
    },
    filterWithArea() {
      this.$refs.map.filterWithArea();
    },
    filterByPRG(level, parcels) {
      if (this[level] != undefined) {
        return parcels.filter(g => {
          return g[level] == this[level].jpt_nazwa_;
        });
      }
      return parcels;
    },
    getPRG(level) {
      const woj = this.wojewodztwo ? this.wojewodztwo.jpt_kod_je : null;
      const pow = this.powiat ? this.powiat.jpt_kod_je : null;
      let payload = {
        level,
        params: {
          woj,
          pow
        }
      };
      if (woj) {
        this.$store.dispatch('getPRG', payload).then(r => {
          if (r.status === 200) {
            this[level] = r.data[level];
          }
        });
      }
    },
    prepareDeleteParcel(item) {
      this.confirmDialogLoaded = true;
      this.confirmDialogVisible = true;
      this.itemToDelete = item;
    },
    createOffer() {
      this.creatingOffer = true;
      this.$store
        .dispatch('postOfferFromParcels', {
          ids: this.ids
        })
        .then(r => {
          this.creatingOffer = false;
          if (r.status === 201) {
            if (r.data.here_api_error) {
              this.$root.$emit('showSnackbar', this.$i18n.t(`default.warningHere`));
            }
            this.$router.push({
              name: 'offer',
              params: { id: r.data.offer }
            });
          } else if (r.status === 503) {
            this.$root.$emit('showSnackbar', this.$i18n.t(`default.errorHere`));
          } else {
            this.$root.$emit('showSnackbar', this.$i18n.t(`default.error`));
          }
        })
        .catch(e => {
          this.creatingOffer = false;
          if (e.response && e.response.data && e.response.data.error) {
            if (e.response.data.error === "Invalid geometry. Parcels geometries are probably self-intersecting") {
              this.$root.$emit('showSnackbar', this.$i18n.t(`default.intersectingParcelsGeometries`));
            } else {
              this.$root.$emit('showSnackbar', e.response.data.error);
            }
          } else {
            this.$root.$emit('showSnackbar', this.$i18n.t(`default.error`));
          }
        });
    },
    itemExpanded(e) {
      if (e.value) {
        this.expandedItem = e.item.id;
        this.filteredParcels = e.item.parcels.map(parcel => parcel.id);
      } else {
        this.expandedItem = null;
        this.filteredParcels = [];
      }
    },
    print() {
      this.isPrintingVisible = false;
    },
    // runFilter(data) {
    //   this.filters = data;
    //   this.filtersVisible = false;
    // },
    onRowClicked(id) {
      this.selectedRow = id;
      if (this.isParcelEditing) {
        this.endParcelEditing('cancel');
      }
      this.$refs.map.selectParcelById(id);
    },
    selectedRowOnMap(id) {
      this.selectedRow = id;
    },
    setFilteredParcels(parcels) {
      this.filteredParcels = [...new Set(parcels)];
    },
    setFeaturesInView(parcels) {
      this.featuresInView = parcels;
    },
    // Włącz narzędzie rysowania geometrii (dla narzędzia filtrowania i rysowania działki)
    startDrawingPolygon() {
      this.isDrawingActive = true;
      this.$refs.map.startDrawingPolygon('draw');
    },

    zoomToParcel(item) {
      this.$refs.map.zoomToParcelById(item.id);
    },
    reloadParcels() {
      this.tableLoading = true;
      this.contentLoading = true;
      this.$store
        .dispatch('getParcels', {
          all_attributes: true,
          geometry: true
        })
        .then(data => {
          this.parcels = data;
          this.tableLoading = false;
          this.contentLoading = false;
        });
    }
  }
};
</script>

<style lang="scss">
html {
  overflow: hidden;
}
.v-content__wrap {
  overflow-y: auto;
}
div.v-data-table__wrapper.vb-content {
  height: inherit !important;
}
#parcels-card {
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 0px;
}
#table-parcels {
  height: 100%;
}
.table__row--background {
  background-color: rgba($primary-color, 0.2) !important;
}
#table-owners > div.v-data-table__wrapper.vb-content > table > tbody > tr > td {
  overflow: hidden;
  max-width: 300px !important;
  word-wrap: break-word;
}
.project__button--active {
  border: 1px solid var(--v-primary-base) !important;
}
</style>
